import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CalendarCheck, ChevronRight, ClipboardX, Users } from "lucide-react";
import { getQuickStats } from "../../redux/action/Stats";
import { getHref } from "../../utils/navigation";

const TONES = {
  blue: { tile: "bg-[#EFF6FF] text-[#2563EB]", badge: "bg-[#EFF6FF] text-[#2563EB]" },
  teal: { tile: "bg-[#F0FDFA] text-[#0D9488]", badge: "bg-[#ECFDF5] text-[#059669]" },
  orange: { tile: "bg-[#FFF7ED] text-[#EA580C]", badge: "bg-[#FFF7ED] text-[#EA580C]" },
};

const StatCard = ({
  icon: Icon,
  tone,
  value,
  title,
  sub1,
  sub2,
  hrefTotal,
  hrefTwo,
  hrefThree,
}) => (
  <div className="flex flex-col bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-3 w-full shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-1.5">
      <div
        className={`flex items-center justify-center w-[38px] h-[38px] rounded-xl ${TONES[tone].tile}`}
      >
        <Icon size={18} />
      </div>
      <a
        href={getHref(hrefTwo)}
        className={`no-underline px-2 py-0.5 rounded-full text-[13px] font-semibold font-poppins ${TONES[tone].badge}`}
      >
        {sub1}
      </a>
    </div>
    <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">
      {value}
    </div>
    <a
      href={getHref(hrefTotal)}
      className="no-underline mt-0.5 text-[13px] uppercase tracking-wide font-medium text-[#64748B] font-poppins"
    >
      {title}
    </a>
    <a
      href={getHref(hrefThree)}
      className="no-underline mt-2 pt-2 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9] flex items-center justify-between text-[14px] font-medium text-[#334155] font-poppins hover:text-[#2563EB]"
    >
      <span>{sub2}</span>
      <ChevronRight size={14} className="text-[#94A3B8]" />
    </a>
  </div>
);

const QuickStats = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state) => state.GET_QUICK_STATS
  );
  const [counsellorId, setCounsellorId] = useState(null);

  const [type, setType] = useState("ThisWeek");
  const getQuickStatsData = () => {
    let payload;
    if (type === "ThisWeek") {
      payload = {
        counsellorId: localStorage.getItem("cid"),
        currentWeek: true,
      };
    } else if (type === "ThisMonth") {
      payload = {
        counsellorId: localStorage.getItem("cid"),
        currentMonth: true,
      };
    } else if (type === "Last3Month") {
      payload = {
        counsellorId: localStorage.getItem("cid"),
        lastThreeMonth: true,
      };
    }
    dispatch(getQuickStats(payload));
  };
  useEffect(() => {
    const interval = setInterval(() => {
      const cid = localStorage.getItem("cid");
      if (cid) {
        setCounsellorId(cid);
        clearInterval(interval);
      }
    }, 500); // check every 500ms until it's set

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (counsellorId) {
      getQuickStatsData();
    }
  }, [type, counsellorId]);

  return (
    <div className="p-1 mb-3">
      {/* Header with title and dropdown */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[#171717] text-base font-semibold font-poppins">
          Quick Stats
        </h2>
        <div className="relative inline-block">
          <select
            className="border-[1px] border-solid border-[#E2E8F0] font-poppins text-xs rounded-xl pl-[12px] pr-[34px] py-[6px] appearance-none bg-white text-[#2563EB] font-medium cursor-pointer hover:border-[#CBD5E1]"
            onChange={(e) => setType(e.target.value)}
          >
            <option value={"ThisWeek"}>This Week</option>
            <option value={"ThisMonth"}>This Month</option>
            <option value={"Last3Month"}>3 Months</option>
          </select>

          {/* Custom arrow */}
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            <svg
              className="w-3.5 h-3.5 text-[#2563EB]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        <StatCard
          icon={CalendarCheck}
          tone="blue"
          value={
            (data?.completedAppointments || 0) +
            (data?.upcomingAppointments || 0)
          }
          title="Total Appointments"
          sub1={`${data?.upcomingAppointments || 0} Upcoming`}
          sub2={`${data?.completedAppointments || 0} Completed`}
          hrefTotal={"/upcoming-appointments/all"}
          hrefTwo={"/upcoming-appointments/upcoming"}
          hrefThree={"/upcoming-appointments/past"}
        />
        <StatCard
          icon={Users}
          tone="teal"
          value={(data?.newPatients || 0) + (data?.returningPatients || 0)}
          title="Session Details"
          sub1={`${data?.newPatients || 0} New`}
          sub2={`${data?.returningPatients || 0} Returning`}
          hrefTotal={"/upcoming-appointments/all"}
          hrefTwo={"/upcoming-appointments/new_user"}
          hrefThree={"/upcoming-appointments/returning"}
        />
        <StatCard
          icon={ClipboardX}
          tone="orange"
          value={
            (data?.missedAppointments || 0) + (data?.missedPrescriptions || 0)
          }
          title="Missed Tasks"
          sub1={`${data?.missedAppointments || 0} Appt`}
          sub2={`${data?.missedPrescriptions || 0} Recommendations`}
          hrefTotal={"/upcoming-appointments/past"}
          hrefTwo={"/upcoming-appointments/did_not_joined"}
          hrefThree={"/upcoming-appointments/incomplete"}
        />
      </div>
    </div>
  );
};

export default QuickStats;
